import { r as registerInstance, c as createEvent, h, H as Host, g as getElement } from './index-8f7fb048.js';
import { M as MENU_SLOT_NAMES } from './menu-17e8abf2.js';
import { g as getNamespacedTagFor } from './index-739b155e.js';
import { r as requiredArgs, t as toDate, a as toInteger, g as getDefaultOptions, i as isBefore, b as isAfter, c as isSameDay } from './index-39ffa48f.js';

/**
 * @name addDays
 * @category Day Helpers
 * @summary Add the specified number of days to the given date.
 *
 * @description
 * Add the specified number of days to the given date.
 *
 * @param {Date|Number} date - the date to be changed
 * @param {Number} amount - the amount of days to be added. Positive decimals will be rounded using `Math.floor`, decimals less than zero will be rounded using `Math.ceil`.
 * @returns {Date} - the new date with the days added
 * @throws {TypeError} - 2 arguments required
 *
 * @example
 * // Add 10 days to 1 September 2014:
 * const result = addDays(new Date(2014, 8, 1), 10)
 * //=> Thu Sep 11 2014 00:00:00
 */
function addDays(dirtyDate, dirtyAmount) {
  requiredArgs(2, arguments);
  var date = toDate(dirtyDate);
  var amount = toInteger(dirtyAmount);
  if (isNaN(amount)) {
    return new Date(NaN);
  }
  if (!amount) {
    // If 0 days, no-op to avoid changing times in the hour before end of DST
    return date;
  }
  date.setDate(date.getDate() + amount);
  return date;
}

/**
 * @name addMonths
 * @category Month Helpers
 * @summary Add the specified number of months to the given date.
 *
 * @description
 * Add the specified number of months to the given date.
 *
 * @param {Date|Number} date - the date to be changed
 * @param {Number} amount - the amount of months to be added. Positive decimals will be rounded using `Math.floor`, decimals less than zero will be rounded using `Math.ceil`.
 * @returns {Date} the new date with the months added
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // Add 5 months to 1 September 2014:
 * const result = addMonths(new Date(2014, 8, 1), 5)
 * //=> Sun Feb 01 2015 00:00:00
 */
function addMonths(dirtyDate, dirtyAmount) {
  requiredArgs(2, arguments);
  var date = toDate(dirtyDate);
  var amount = toInteger(dirtyAmount);
  if (isNaN(amount)) {
    return new Date(NaN);
  }
  if (!amount) {
    // If 0 months, no-op to avoid changing times in the hour before end of DST
    return date;
  }
  var dayOfMonth = date.getDate();

  // The JS Date object supports date math by accepting out-of-bounds values for
  // month, day, etc. For example, new Date(2020, 0, 0) returns 31 Dec 2019 and
  // new Date(2020, 13, 1) returns 1 Feb 2021.  This is *almost* the behavior we
  // want except that dates will wrap around the end of a month, meaning that
  // new Date(2020, 13, 31) will return 3 Mar 2021 not 28 Feb 2021 as desired. So
  // we'll default to the end of the desired month by adding 1 to the desired
  // month and using a date of 0 to back up one day to the end of the desired
  // month.
  var endOfDesiredMonth = new Date(date.getTime());
  endOfDesiredMonth.setMonth(date.getMonth() + amount + 1, 0);
  var daysInMonth = endOfDesiredMonth.getDate();
  if (dayOfMonth >= daysInMonth) {
    // If we're already at the end of the month, then this is the correct date
    // and we're done.
    return endOfDesiredMonth;
  } else {
    // Otherwise, we now know that setting the original day-of-month value won't
    // cause an overflow, so set the desired day-of-month. Note that we can't
    // just set the date of `endOfDesiredMonth` because that object may have had
    // its time changed in the unusual case where where a DST transition was on
    // the last day of the month and its local time was in the hour skipped or
    // repeated next to a DST transition.  So we use `date` instead which is
    // guaranteed to still have the original time.
    date.setFullYear(endOfDesiredMonth.getFullYear(), endOfDesiredMonth.getMonth(), dayOfMonth);
    return date;
  }
}

/**
 * @name startOfWeek
 * @category Week Helpers
 * @summary Return the start of a week for the given date.
 *
 * @description
 * Return the start of a week for the given date.
 * The result will be in the local timezone.
 *
 * @param {Date|Number} date - the original date
 * @param {Object} [options] - an object with options.
 * @param {Locale} [options.locale=defaultLocale] - the locale object. See [Locale]{@link https://date-fns.org/docs/Locale}
 * @param {0|1|2|3|4|5|6} [options.weekStartsOn=0] - the index of the first day of the week (0 - Sunday)
 * @returns {Date} the start of a week
 * @throws {TypeError} 1 argument required
 * @throws {RangeError} `options.weekStartsOn` must be between 0 and 6
 *
 * @example
 * // The start of a week for 2 September 2014 11:55:00:
 * const result = startOfWeek(new Date(2014, 8, 2, 11, 55, 0))
 * //=> Sun Aug 31 2014 00:00:00
 *
 * @example
 * // If the week starts on Monday, the start of the week for 2 September 2014 11:55:00:
 * const result = startOfWeek(new Date(2014, 8, 2, 11, 55, 0), { weekStartsOn: 1 })
 * //=> Mon Sep 01 2014 00:00:00
 */
function startOfWeek(dirtyDate, options) {
  var _ref, _ref2, _ref3, _options$weekStartsOn, _options$locale, _options$locale$optio, _defaultOptions$local, _defaultOptions$local2;
  requiredArgs(1, arguments);
  var defaultOptions = getDefaultOptions();
  var weekStartsOn = toInteger((_ref = (_ref2 = (_ref3 = (_options$weekStartsOn = options === null || options === void 0 ? void 0 : options.weekStartsOn) !== null && _options$weekStartsOn !== void 0 ? _options$weekStartsOn : options === null || options === void 0 ? void 0 : (_options$locale = options.locale) === null || _options$locale === void 0 ? void 0 : (_options$locale$optio = _options$locale.options) === null || _options$locale$optio === void 0 ? void 0 : _options$locale$optio.weekStartsOn) !== null && _ref3 !== void 0 ? _ref3 : defaultOptions.weekStartsOn) !== null && _ref2 !== void 0 ? _ref2 : (_defaultOptions$local = defaultOptions.locale) === null || _defaultOptions$local === void 0 ? void 0 : (_defaultOptions$local2 = _defaultOptions$local.options) === null || _defaultOptions$local2 === void 0 ? void 0 : _defaultOptions$local2.weekStartsOn) !== null && _ref !== void 0 ? _ref : 0);

  // Test if weekStartsOn is between 0 and 6 _and_ is not NaN
  if (!(weekStartsOn >= 0 && weekStartsOn <= 6)) {
    throw new RangeError('weekStartsOn must be between 0 and 6 inclusively');
  }
  var date = toDate(dirtyDate);
  var day = date.getDay();
  var diff = (day < weekStartsOn ? 7 : 0) + day - weekStartsOn;
  date.setDate(date.getDate() - diff);
  date.setHours(0, 0, 0, 0);
  return date;
}

/**
 * @name addWeeks
 * @category Week Helpers
 * @summary Add the specified number of weeks to the given date.
 *
 * @description
 * Add the specified number of week to the given date.
 *
 * @param {Date|Number} date - the date to be changed
 * @param {Number} amount - the amount of weeks to be added. Positive decimals will be rounded using `Math.floor`, decimals less than zero will be rounded using `Math.ceil`.
 * @returns {Date} the new date with the weeks added
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // Add 4 weeks to 1 September 2014:
 * const result = addWeeks(new Date(2014, 8, 1), 4)
 * //=> Mon Sep 29 2014 00:00:00
 */
function addWeeks(dirtyDate, dirtyAmount) {
  requiredArgs(2, arguments);
  var amount = toInteger(dirtyAmount);
  var days = amount * 7;
  return addDays(dirtyDate, days);
}

/**
 * @name addYears
 * @category Year Helpers
 * @summary Add the specified number of years to the given date.
 *
 * @description
 * Add the specified number of years to the given date.
 *
 * @param {Date|Number} date - the date to be changed
 * @param {Number} amount - the amount of years to be added. Positive decimals will be rounded using `Math.floor`, decimals less than zero will be rounded using `Math.ceil`.
 * @returns {Date} the new date with the years added
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // Add 5 years to 1 September 2014:
 * const result = addYears(new Date(2014, 8, 1), 5)
 * //=> Sun Sep 01 2019 00:00:00
 */
function addYears(dirtyDate, dirtyAmount) {
  requiredArgs(2, arguments);
  var amount = toInteger(dirtyAmount);
  return addMonths(dirtyDate, amount * 12);
}

/**
 * @name startOfMonth
 * @category Month Helpers
 * @summary Return the start of a month for the given date.
 *
 * @description
 * Return the start of a month for the given date.
 * The result will be in the local timezone.
 *
 * @param {Date|Number} date - the original date
 * @returns {Date} the start of a month
 * @throws {TypeError} 1 argument required
 *
 * @example
 * // The start of a month for 2 September 2014 11:55:00:
 * const result = startOfMonth(new Date(2014, 8, 2, 11, 55, 0))
 * //=> Mon Sep 01 2014 00:00:00
 */
function startOfMonth(dirtyDate) {
  requiredArgs(1, arguments);
  var date = toDate(dirtyDate);
  date.setDate(1);
  date.setHours(0, 0, 0, 0);
  return date;
}

/**
 * @name startOfYear
 * @category Year Helpers
 * @summary Return the start of a year for the given date.
 *
 * @description
 * Return the start of a year for the given date.
 * The result will be in the local timezone.
 *
 * @param {Date|Number} date - the original date
 * @returns {Date} the start of a year
 * @throws {TypeError} 1 argument required
 *
 * @example
 * // The start of a year for 2 September 2014 11:55:00:
 * const result = startOfYear(new Date(2014, 8, 2, 11, 55, 00))
 * //=> Wed Jan 01 2014 00:00:00
 */
function startOfYear(dirtyDate) {
  requiredArgs(1, arguments);
  var cleanDate = toDate(dirtyDate);
  var date = new Date(0);
  date.setFullYear(cleanDate.getFullYear(), 0, 1);
  date.setHours(0, 0, 0, 0);
  return date;
}

/**
 * @name endOfWeek
 * @category Week Helpers
 * @summary Return the end of a week for the given date.
 *
 * @description
 * Return the end of a week for the given date.
 * The result will be in the local timezone.
 *
 * @param {Date|Number} date - the original date
 * @param {Object} [options] - an object with options.
 * @param {Locale} [options.locale=defaultLocale] - the locale object. See [Locale]{@link https://date-fns.org/docs/Locale}
 * @param {0|1|2|3|4|5|6} [options.weekStartsOn=0] - the index of the first day of the week (0 - Sunday)
 * @returns {Date} the end of a week
 * @throws {TypeError} 1 argument required
 * @throws {RangeError} `options.weekStartsOn` must be between 0 and 6
 *
 * @example
 * // The end of a week for 2 September 2014 11:55:00:
 * const result = endOfWeek(new Date(2014, 8, 2, 11, 55, 0))
 * //=> Sat Sep 06 2014 23:59:59.999
 *
 * @example
 * // If the week starts on Monday, the end of the week for 2 September 2014 11:55:00:
 * const result = endOfWeek(new Date(2014, 8, 2, 11, 55, 0), { weekStartsOn: 1 })
 * //=> Sun Sep 07 2014 23:59:59.999
 */
function endOfWeek(dirtyDate, options) {
  var _ref, _ref2, _ref3, _options$weekStartsOn, _options$locale, _options$locale$optio, _defaultOptions$local, _defaultOptions$local2;
  requiredArgs(1, arguments);
  var defaultOptions = getDefaultOptions();
  var weekStartsOn = toInteger((_ref = (_ref2 = (_ref3 = (_options$weekStartsOn = options === null || options === void 0 ? void 0 : options.weekStartsOn) !== null && _options$weekStartsOn !== void 0 ? _options$weekStartsOn : options === null || options === void 0 ? void 0 : (_options$locale = options.locale) === null || _options$locale === void 0 ? void 0 : (_options$locale$optio = _options$locale.options) === null || _options$locale$optio === void 0 ? void 0 : _options$locale$optio.weekStartsOn) !== null && _ref3 !== void 0 ? _ref3 : defaultOptions.weekStartsOn) !== null && _ref2 !== void 0 ? _ref2 : (_defaultOptions$local = defaultOptions.locale) === null || _defaultOptions$local === void 0 ? void 0 : (_defaultOptions$local2 = _defaultOptions$local.options) === null || _defaultOptions$local2 === void 0 ? void 0 : _defaultOptions$local2.weekStartsOn) !== null && _ref !== void 0 ? _ref : 0);

  // Test if weekStartsOn is between 0 and 6 _and_ is not NaN
  if (!(weekStartsOn >= 0 && weekStartsOn <= 6)) {
    throw new RangeError('weekStartsOn must be between 0 and 6 inclusively');
  }
  var date = toDate(dirtyDate);
  var day = date.getDay();
  var diff = (day < weekStartsOn ? -7 : 0) + 6 - (day - weekStartsOn);
  date.setDate(date.getDate() + diff);
  date.setHours(23, 59, 59, 999);
  return date;
}

/**
 * @name lastDayOfMonth
 * @category Month Helpers
 * @summary Return the last day of a month for the given date.
 *
 * @description
 * Return the last day of a month for the given date.
 * The result will be in the local timezone.
 *
 * @param {Date|Number} date - the original date
 * @returns {Date} the last day of a month
 * @throws {TypeError} 1 argument required
 *
 * @example
 * // The last day of a month for 2 September 2014 11:55:00:
 * const result = lastDayOfMonth(new Date(2014, 8, 2, 11, 55, 0))
 * //=> Tue Sep 30 2014 00:00:00
 */
function lastDayOfMonth(dirtyDate) {
  requiredArgs(1, arguments);
  var date = toDate(dirtyDate);
  var month = date.getMonth();
  date.setFullYear(date.getFullYear(), month + 1, 0);
  date.setHours(0, 0, 0, 0);
  return date;
}

/**
 * @name lastDayOfYear
 * @category Year Helpers
 * @summary Return the last day of a year for the given date.
 *
 * @description
 * Return the last day of a year for the given date.
 * The result will be in the local timezone.
 *
 * @param {Date|Number} date - the original date
 * @returns {Date} the last day of a year
 * @throws {TypeError} 1 argument required
 *
 * @example
 * // The last day of a year for 2 September 2014 11:55:00:
 * const result = lastDayOfYear(new Date(2014, 8, 2, 11, 55, 00))
 * //=> Wed Dec 31 2014 00:00:00
 */
function lastDayOfYear(dirtyDate) {
  requiredArgs(1, arguments);
  var date = toDate(dirtyDate);
  var year = date.getFullYear();
  date.setFullYear(year + 1, 0, 0);
  date.setHours(0, 0, 0, 0);
  return date;
}

/**
 * @name startOfYesterday
 * @category Day Helpers
 * @summary Return the start of yesterday.
 * @pure false
 *
 * @description
 * Return the start of yesterday.
 *
 * > ⚠️ Please note that this function is not present in the FP submodule as
 * > it uses `new Date()` internally hence impure and can't be safely curried.
 *
 * @returns {Date} the start of yesterday
 *
 * @example
 * // If today is 6 October 2014:
 * const result = startOfYesterday()
 * //=> Sun Oct 5 2014 00:00:00
 */
function startOfYesterday() {
  var now = new Date();
  var year = now.getFullYear();
  var month = now.getMonth();
  var day = now.getDate();
  var date = new Date(0);
  date.setFullYear(year, month, day - 1);
  date.setHours(0, 0, 0, 0);
  return date;
}

/**
 * @name subMonths
 * @category Month Helpers
 * @summary Subtract the specified number of months from the given date.
 *
 * @description
 * Subtract the specified number of months from the given date.
 *
 * @param {Date|Number} date - the date to be changed
 * @param {Number} amount - the amount of months to be subtracted. Positive decimals will be rounded using `Math.floor`, decimals less than zero will be rounded using `Math.ceil`.
 * @returns {Date} the new date with the months subtracted
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // Subtract 5 months from 1 February 2015:
 * const result = subMonths(new Date(2015, 1, 1), 5)
 * //=> Mon Sep 01 2014 00:00:00
 */
function subMonths(dirtyDate, dirtyAmount) {
  requiredArgs(2, arguments);
  var amount = toInteger(dirtyAmount);
  return addMonths(dirtyDate, -amount);
}

/**
 * @name subWeeks
 * @category Week Helpers
 * @summary Subtract the specified number of weeks from the given date.
 *
 * @description
 * Subtract the specified number of weeks from the given date.
 *
 * @param {Date|Number} date - the date to be changed
 * @param {Number} amount - the amount of weeks to be subtracted. Positive decimals will be rounded using `Math.floor`, decimals less than zero will be rounded using `Math.ceil`.
 * @returns {Date} the new date with the weeks subtracted
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // Subtract 4 weeks from 1 September 2014:
 * const result = subWeeks(new Date(2014, 8, 1), 4)
 * //=> Mon Aug 04 2014 00:00:00
 */
function subWeeks(dirtyDate, dirtyAmount) {
  requiredArgs(2, arguments);
  var amount = toInteger(dirtyAmount);
  return addWeeks(dirtyDate, -amount);
}

/**
 * @name subYears
 * @category Year Helpers
 * @summary Subtract the specified number of years from the given date.
 *
 * @description
 * Subtract the specified number of years from the given date.
 *
 * @param {Date|Number} date - the date to be changed
 * @param {Number} amount - the amount of years to be subtracted. Positive decimals will be rounded using `Math.floor`, decimals less than zero will be rounded using `Math.ceil`.
 * @returns {Date} the new date with the years subtracted
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // Subtract 5 years from 1 September 2014:
 * const result = subYears(new Date(2014, 8, 1), 5)
 * //=> Tue Sep 01 2009 00:00:00
 */
function subYears(dirtyDate, dirtyAmount) {
  requiredArgs(2, arguments);
  var amount = toInteger(dirtyAmount);
  return addYears(dirtyDate, -amount);
}

const marketDatePickerCss = ":host{display:flex}@media only screen and (max-width: 800px){:host([mobile-menu-position='top']){flex-direction:column}:host([mobile-menu-position='top']) list-view{margin-bottom:var(--date-picker-menu-vertical-spacing)}:host([mobile-menu-position='bottom']){flex-direction:column-reverse}:host([mobile-menu-position='bottom']) list-view{margin-top:var(--date-picker-menu-vertical-spacing)}}.left-nav{margin-right:var(--date-picker-header-button-horizontal-spacing)}header{display:flex;justify-content:space-between;align-items:center;height:var(--date-picker-header-minimum-height);margin-bottom:var(--date-picker-header-vertical-spacing)}nav{display:flex}group-view{display:grid;grid-template-rows:repeat(auto-fill, 1fr);grid-template-columns:repeat(7, 1fr);gap:var(--date-picker-grid-item-vertical-padding) var(--date-picker-grid-item-horizontal-padding);box-sizing:border-box}";

/**
 * Object class to hold necessary date information when building out each market-date-picker-date for the calendar
 */
class Day {
  constructor() {
    this.date = '';
    this.month = null;
    this.year = null;
    // For market-date-picker-date props
    this.selected = false;
    this.selection = 'none';
    this.today = false;
    this.disabled = false;
  }
}
const MarketDatePicker = class {
  constructor(hostRef) {
    registerInstance(this, hostRef);
    this.marketDateRangeChanged = createEvent(this, "marketDateRangeChanged", 7);
    this.marketMenuSelectionChanged = createEvent(this, "marketMenuSelectionChanged", 7);
    this.selectionType = 'single';
    this.selectedStartDate = undefined;
    this.selectedEndDate = undefined;
    this.presetMenuOption = undefined;
    this.displayMenu = false;
    this.mobileMenuPosition = 'top';
    this.excludeMenuItems = '';
    this.timeframe = 'present';
    this.isDateDisabled = undefined;
    this.locale = navigator.language || 'en-US';
    this.displayedDate = undefined;
    this.withInputs = '';
    this.invalid = false;
    this.displayedMonth = undefined;
    this.displayedWeekdays = [];
    this.hoveredDate = undefined;
  }
  /**
   * Build out the weekday headers based on locale for the calendar view.
   */
  buildWeekdays() {
    this.displayedWeekdays = [];
    // Grab start of the week
    const weekday = startOfWeek(new Date());
    let weekdayText;
    for (let d = 0; d < 7; d++) {
      weekdayText = weekday.toLocaleDateString(this.locale, { weekday: 'short' });
      // Shorten English based weekday headers by one
      if (this.locale.startsWith('en-')) {
        weekdayText = weekdayText.slice(0, -1);
      }
      this.displayedWeekdays.push(weekdayText);
      weekday.setDate(weekday.getDate() + 1);
    }
  }
  /**
   * Compares current hovered date to selected dates to determine correct styling for selection.
   */
  addHoveredDateRangeStyling(currentDate, datePickerDay) {
    if (!this.hoveredDate) {
      return;
    }
    const currentStartDate = new Date(this.selectedStartDate);
    // Set the day to be in the middle of a range when there is a start date and no end date,
    // and the day in question is between the start date and hovered date.
    if (this.selectedStartDate &&
      !this.selectedEndDate &&
      isBefore(currentDate, this.hoveredDate) &&
      isAfter(currentDate, currentStartDate)) {
      datePickerDay.selection = 'range-middle';
    }
    if (!isSameDay(this.hoveredDate, currentDate)) {
      return;
    }
    // If there's no start date, or a date range is selected, or the hovered date is before or the same as the start date,
    // the hovered button should be the start of a new range.
    if (!this.selectedStartDate ||
      (this.selectedStartDate && this.selectedEndDate) ||
      !isAfter(this.hoveredDate, currentStartDate)) {
      datePickerDay.selection = 'range-first';
    }
    else {
      datePickerDay.selection = 'range-last';
    }
  }
  /**
   * Helper function to add selection metadata to the date object being processed in buildCalendar.
   */
  addDateSelectionAttributes(day, calendarDate, currentStartDate, currentEndDate) {
    // Checks for start date comparison (for both single/range types)
    if (currentStartDate && isSameDay(calendarDate, currentStartDate)) {
      day.selected = true;
      day.selection = this.selectionType === 'single' ? 'single' : 'range-first';
    }
    // For range types only, check for dates within a selected range, or ends in a range
    if (this.selectionType === 'range') {
      this.addHoveredDateRangeStyling(calendarDate, day);
      if (currentStartDate &&
        currentEndDate &&
        isAfter(calendarDate, currentStartDate) &&
        isBefore(calendarDate, currentEndDate)) {
        day.selected = true;
        day.selection = 'range-middle';
      }
      if (currentEndDate && isSameDay(calendarDate, currentEndDate)) {
        day.selected = true;
        day.selection = 'range-last';
      }
    }
  }
  /**
   * The main chunk of this component is building out the calendar view.
   * Ensures correct styling and formatting is placed on each date component.
   * Adapted from: https://ionicframework.com/blog/building-with-stencil-calendar-component/
   */
  buildCalendar() {
    const today = new Date();
    const currentDisplayedDate = new Date(this.displayedDate);
    // Calendar used in iteration
    const calendar = new Date(currentDisplayedDate.getFullYear(), currentDisplayedDate.getMonth(), 1);
    // First day of month may not be first day of week
    // Roll back until first day of week
    calendar.setDate(calendar.getDate() - calendar.getDay());
    // Clear days to be rendered
    this.days = [];
    // Create date objects from selected dates if they exist
    const currentStartDate = this.selectedStartDate && new Date(this.selectedStartDate);
    const currentEndDate = this.selectedEndDate && new Date(this.selectedEndDate);
    for (let d = 0; d < 42; d++) {
      // Day to be rendered
      // Seed with current date in iteration
      const day = new Day();
      day.year = calendar.getFullYear();
      day.month = calendar.getMonth();
      // Populate day in month
      // Undefined date properties are not rendered
      if (calendar.getFullYear() === currentDisplayedDate.getFullYear() &&
        calendar.getMonth() === currentDisplayedDate.getMonth()) {
        day.date = calendar.getDate().toString();
      }
      // Check for today
      if (isSameDay(calendar, today)) {
        day.today = true;
      }
      // Check for selection types
      this.addDateSelectionAttributes(day, calendar, currentStartDate, currentEndDate);
      // Check for disabled dates
      if (day.date && this.isDateDisabled) {
        day.disabled = this.isDateDisabled(calendar);
      }
      if (this.timeframe === 'future' && isBefore(calendar, today) && !isSameDay(calendar, today)) {
        day.disabled = true;
      }
      if (this.timeframe === 'past' && isAfter(calendar, today) && !isSameDay(calendar, today)) {
        day.disabled = true;
      }
      // Add to days to be rendered
      this.days.push(day);
      // Move to next date
      calendar.setDate(calendar.getDate() + 1);
      // Do not render the last week
      // Depending on calendar layout
      // Some months require five weeks
      // Others six weeks (see May 2021)
      if (calendar.getDay() === 0 && calendar.getMonth() !== currentDisplayedDate.getMonth()) {
        break;
      }
    }
  }
  /**
   * Helper function to clear date selections.
   */
  clearDateSelections() {
    this.selectedStartDate = null;
    this.selectedEndDate = null;
  }
  /**
   * Used for the navigation arrows in the market-date-picker header, moves the calendar forward/backward by increment.
   * Currently only used for +1/-1 month.
   */
  navigateMonth(increment) {
    const currentDisplayedDate = new Date(this.displayedDate);
    this.displayedDate = new Date(currentDisplayedDate.getFullYear(), currentDisplayedDate.getMonth() + increment, 1).toISOString();
  }
  /**
   *  When ever an event changes the selected date, this updates the calendar view.
   *  The event receives a date string in the yyyy-mm-dd or yyy-mm-ddThh:mm format.
   *  this then checks what parts of the date have changed and only sends the changed parts to the
   * _selectDate function that receives (day, month, year)
   */
  dateInputDateSet({ detail: { date, input } }) {
    const [newDate, time] = date.split('T');
    const [newYear, newMonth, newDay] = newDate.split('-').map(Number);
    this.displayedDate = new Date(newYear, newMonth - 1, 1).toString();
    // fallback to undefined, to prevent overwriting if time was initialized but withTime is disabled.
    const [hour, minute] = this.withInputs === 'date-and-time' ? time.split(':').map(Number) : [];
    this._selectDate(newDay, newMonth - 1, newYear, hour, minute, input);
  }
  /**
   * Whenever a market-date-picker-date is selected, this updates the selected date props, which then updates the calendar view.
   */
  selectDate({ detail: { date: dateElement } }) {
    this._selectDate(dateElement.day);
  }
  /**
   * Whenever a market-date-picker-date is moused over or hovered, this updates the hovered date to update the calendar view.
   */
  hoverDate({ detail: { date: dateElement } }) {
    const currentDisplayedDate = new Date(this.displayedDate);
    this.hoveredDate = new Date(currentDisplayedDate.getFullYear(), currentDisplayedDate.getMonth(), dateElement.day);
  }
  /**
   * Whenever a market-date-picker-date is moused out, clear any hovering styling in the calendar view.
   */
  mousedOutDate() {
    this.hoveredDate = null;
  }
  /**
   * Whenever a market-row within market-date-picker-menu is selected, this ensures we move the current selected dates
   * to whichever option the user has selected.
   */
  selectMenuRow(e) {
    const menuOption = e.detail.menuSelection;
    if (!menuOption) {
      return;
    }
    this.marketMenuSelectionChanged.emit({
      menuSelection: menuOption,
    });
    const prevStartDate = this.selectedStartDate;
    const prevEndDate = this.selectedEndDate;
    this._setMenuRowOption(menuOption);
    if (menuOption !== MENU_SLOT_NAMES.CUSTOM) {
      this.marketDateRangeChanged.emit({
        prevStartDate,
        prevEndDate,
        startDate: this.selectedStartDate,
        endDate: this.selectedEndDate,
        menuSelection: menuOption,
      });
    }
  }
  /**
   * Updates invalid state of date picker based on date input selections
   */
  setInvalidState({ detail: { invalid } }) {
    this.invalid = invalid;
  }
  _setMenuRowOption(menuOption) {
    const today = new Date();
    switch (menuOption) {
      case MENU_SLOT_NAMES.TODAY:
        this.clearDateSelections();
        this.selectedStartDate = today.toISOString();
        break;
      case MENU_SLOT_NAMES.YESTERDAY:
        this.clearDateSelections();
        this.selectedStartDate = startOfYesterday().toISOString();
        break;
      case MENU_SLOT_NAMES.THIS_WEEK:
        this.selectedStartDate = startOfWeek(today).toISOString();
        if (this.selectionType === 'range') {
          this.selectedEndDate = endOfWeek(today).toISOString();
        }
        break;
      case MENU_SLOT_NAMES.LAST_WEEK: {
        const lastWeekDate = subWeeks(today, 1);
        this.selectedStartDate = startOfWeek(lastWeekDate).toISOString();
        if (this.selectionType === 'range') {
          this.selectedEndDate = endOfWeek(lastWeekDate).toISOString();
        }
        break;
      }
      case MENU_SLOT_NAMES.THIS_MONTH:
        this.selectedStartDate = startOfMonth(today).toISOString();
        if (this.selectionType === 'range') {
          this.selectedEndDate = lastDayOfMonth(today).toISOString();
        }
        break;
      case MENU_SLOT_NAMES.LAST_MONTH: {
        const lastMonthDate = subMonths(today, 1);
        this.selectedStartDate = startOfMonth(lastMonthDate).toISOString();
        if (this.selectionType === 'range') {
          this.selectedEndDate = lastDayOfMonth(lastMonthDate).toISOString();
        }
        break;
      }
      case MENU_SLOT_NAMES.THIS_YEAR:
        this.selectedStartDate = startOfYear(today).toISOString();
        if (this.selectionType === 'range') {
          this.selectedEndDate = lastDayOfYear(today).toISOString();
        }
        break;
      case MENU_SLOT_NAMES.LAST_YEAR: {
        const lastYearDate = subYears(today, 1);
        this.selectedStartDate = startOfYear(lastYearDate).toISOString();
        if (this.selectionType === 'range') {
          this.selectedEndDate = lastDayOfYear(lastYearDate).toISOString();
        }
        break;
      }
    }
    // Update displayed date to selected date
    if (menuOption !== MENU_SLOT_NAMES.CUSTOM) {
      this.displayedDate = this.selectedStartDate;
    }
  }
  componentWillLoad() {
    if (this.presetMenuOption) {
      this._setMenuRowOption(this.presetMenuOption);
    }
  }
  _selectDate(daySelected, selectedMonth, selectedYear, selectedHour, selectedMinute, input) {
    const prevStartDate = this.selectedStartDate;
    const prevEndDate = this.selectedEndDate;
    const currentDisplayedDate = new Date(this.displayedDate);
    const day = new Date(selectedYear || currentDisplayedDate.getFullYear(), selectedMonth || currentDisplayedDate.getMonth(), daySelected || currentDisplayedDate.getDate(), selectedHour || currentDisplayedDate.getHours(), selectedMinute || currentDisplayedDate.getMinutes()).toISOString();
    if (this.displayMenu && !isSameDay(new Date(this.selectedStartDate), new Date(day))) {
      // Select custom menu row if it exists when calendar is updated
      const menu = this.el.shadowRoot.querySelector(getNamespacedTagFor('market-date-picker-menu'));
      menu._selectCustomRow();
    }
    if (this.selectionType === 'single') {
      this.clearDateSelections();
      this.selectedStartDate = day;
    }
    else {
      // Date range logic
      if (this.selectedStartDate && !this.selectedEndDate && day > this.selectedStartDate) {
        // if new day is after selected start date, and end date isn't set, update end date.
        this.selectedEndDate = day;
      }
      else {
        if (input) {
          if (input === 'start')
            this.selectedStartDate = day;
          if (input === 'end')
            this.selectedEndDate = day;
        }
        else {
          this.clearDateSelections();
          this.selectedStartDate = day;
        }
      }
    }
    this.marketDateRangeChanged.emit({
      prevStartDate,
      prevEndDate,
      startDate: this.selectedStartDate,
      endDate: this.selectedEndDate,
      menuSelection: MENU_SLOT_NAMES.CUSTOM,
    });
  }
  /**
   * Core function called whenever props or states are changed to update the calendar view.
   */
  componentWillRender() {
    // Handles invalid dates passed in to displayed date
    if (new Date(this.displayedDate).toString() === 'Invalid Date') {
      this.displayedDate = new Date().toISOString();
    }
    this.displayedMonth = new Date(this.displayedDate).toLocaleString(this.locale, { month: 'short', year: 'numeric' });
    this.buildWeekdays();
    this.buildCalendar();
  }
  render() {
    const MarketDatePickerMenuTagName = getNamespacedTagFor('market-date-picker-menu');
    const MarketDatePickerDateTagName = getNamespacedTagFor('market-date-picker-date');
    const MarketDatePickerInputDateTagName = getNamespacedTagFor('market-date-picker-input-date');
    const MarketButtonTagName = getNamespacedTagFor('market-button');
    return (h(Host, { class: "market-date-picker" }, this.displayMenu && (h("list-view", null, h(MarketDatePickerMenuTagName, { timeframe: this.timeframe, excludeMenuItems: this.excludeMenuItems, presetMenuOption: this.presetMenuOption }, h("slot", { name: MENU_SLOT_NAMES.TODAY, slot: MENU_SLOT_NAMES.TODAY }, "Today"), h("slot", { name: MENU_SLOT_NAMES.YESTERDAY, slot: MENU_SLOT_NAMES.YESTERDAY }, "Yesterday"), h("slot", { name: MENU_SLOT_NAMES.THIS_WEEK, slot: MENU_SLOT_NAMES.THIS_WEEK }, "This week"), h("slot", { name: MENU_SLOT_NAMES.LAST_WEEK, slot: MENU_SLOT_NAMES.LAST_WEEK }, "Last week"), h("slot", { name: MENU_SLOT_NAMES.THIS_MONTH, slot: MENU_SLOT_NAMES.THIS_MONTH }, "This month"), h("slot", { name: MENU_SLOT_NAMES.LAST_MONTH, slot: MENU_SLOT_NAMES.LAST_MONTH }, "Last month"), h("slot", { name: MENU_SLOT_NAMES.THIS_YEAR, slot: MENU_SLOT_NAMES.THIS_YEAR }, "This year"), h("slot", { name: MENU_SLOT_NAMES.LAST_YEAR, slot: MENU_SLOT_NAMES.LAST_YEAR }, "Last year"), h("slot", { name: MENU_SLOT_NAMES.CUSTOM, slot: MENU_SLOT_NAMES.CUSTOM }, "Custom")))), h("calendar", null, h("header", null, h("h2", { id: "date-picker-label" }, this.displayedMonth), h("nav", null, h(MarketButtonTagName, { class: "left-nav", size: "small", "aria-label": "Previous month", onClick: () => this.navigateMonth(-1) }, h("svg", { slot: "icon", width: "15", height: "16", viewBox: "0 0 15 16", fill: "none", xmlns: "http://www.w3.org/2000/svg" }, h("path", { "fill-rule": "evenodd", "clip-rule": "evenodd", d: "M0.292894 7.29291C-0.0976308 7.68343 -0.0976307 8.3166 0.292894 8.70712L7.29289 15.7071L8.70711 14.2929L3.41421 9.00001L15 9.00001L15 7.00001L3.41421 7.00001L8.70711 1.70712L7.29289 0.292907L0.292894 7.29291Z", fill: "var(--icon-button-secondary-rank-normal-state-icon-color)" }))), h(MarketButtonTagName, { size: "small", "aria-label": "Next month", onClick: () => this.navigateMonth(1) }, h("svg", { slot: "icon", width: "15", height: "16", viewBox: "0 0 15 16", fill: "none", xmlns: "http://www.w3.org/2000/svg" }, h("path", { "fill-rule": "evenodd", "clip-rule": "evenodd", d: "M14.7071 7.29291C15.0976 7.68343 15.0976 8.3166 14.7071 8.70712L7.70711 15.7071L6.29289 14.2929L11.5858 9.00001L-3.806e-07 9.00001L-2.93177e-07 7.00001L11.5858 7.00001L6.29289 1.70712L7.70711 0.292907L14.7071 7.29291Z", fill: "var(--icon-button-secondary-rank-normal-state-icon-color)" }))))), h("group-view", { role: "grid", "aria-labelledby": "date-picker-label" }, 
    // Set the weekdays header for the date picker calendar
    this.displayedWeekdays.map((text) => (h(MarketDatePickerDateTagName, { class: 'weekday-header', disabled: true, day: text }))), 
    // Set the available dates
    this.days.map((day) => (h(MarketDatePickerDateTagName, { disabled: day.disabled, selection: day.selection, today: day.today, selected: day.selected, day: day.date || null })))), this.withInputs && (h(MarketDatePickerInputDateTagName, { class: "date-input-top-margin", range: this.selectionType === 'range', isDateDisabled: this.isDateDisabled, selectedStartDate: this.selectedStartDate, selectedEndDate: this.selectedEndDate, withTime: this.withInputs === 'date-and-time', timeframe: this.timeframe }, h("slot", { name: "start-date", slot: "start-date" }, this.selectionType === 'range' ? 'Start ' : '', "Date"), h("slot", { name: "end-date", slot: "end-date" }, "End Date"), h("slot", { name: "start-time", slot: "start-time" }, this.selectionType === 'range' ? 'Start ' : '', "Time"), h("slot", { name: "end-time", slot: "end-time" }, "End Time"))))));
  }
  get el() { return getElement(this); }
};
MarketDatePicker.style = marketDatePickerCss;

export { MarketDatePicker as market_date_picker };
